import { create } from 'zustand'
import type { PickupLocation, RecipientType, YearGroup } from '../lib/types'

export interface WizardGift {
  id?: string // Database ID for existing gifts
  pickupLocation: PickupLocation
  recipientType: RecipientType
  recipientName: string
  fromName: string
  yearGroup: YearGroup
  price: number
}

export interface SantaVisit {
  timeSlotId: string
  childrenNames: string[]
  dietaryRequirements: string
}

interface WizardState {
  // Step tracking
  currentStep: number
  totalSteps: number

  // User details
  email: string
  fullName: string
  phoneNumber: string

  // Gift flow
  wantsGifts: boolean
  pickupLocation: PickupLocation | null
  gifts: WizardGift[]

  // Santa flow
  wantsSanta: boolean
  selectedTimeSlotId: string | null
  slotHeldUntil: string | null // hold expiry from the server, drives the countdown
  slotExpired: boolean
  santaVisit: SantaVisit | null

  // Tokens
  wantsTokensOnly: boolean
  tokenQuantity: number

  // Order
  orderId: string | null

  // Actions
  setStep: (step: number) => void
  nextStep: () => void
  previousStep: () => void
  setUserDetails: (email: string, fullName: string, phoneNumber: string) => void
  setWantsGifts: (wants: boolean) => void
  setWantsSanta: (wants: boolean) => void
  setWantsTokensOnly: (wants: boolean) => void
  setPickupLocation: (location: PickupLocation) => void
  addGift: (gift: WizardGift) => void
  removeGift: (index: number) => void
  updateGift: (index: number, gift: WizardGift) => void
  setSelectedTimeSlotId: (slotId: string) => void
  setSlotHeldUntil: (until: string | null) => void
  expireSlot: () => void
  clearSlot: () => void
  setSantaVisit: (visit: SantaVisit) => void
  setTokenQuantity: (quantity: number) => void
  setOrderId: (id: string) => void
  reset: () => void
}

const TOTAL_STEPS = 8

export const useWizardStore = create<WizardState>((set) => ({
  currentStep: 1,
  totalSteps: TOTAL_STEPS,
  email: '',
  fullName: '',
  phoneNumber: '',
  wantsGifts: false,
  pickupLocation: null,
  gifts: [],
  wantsSanta: true,
  selectedTimeSlotId: null,
  slotHeldUntil: null,
  slotExpired: false,
  santaVisit: null,
  wantsTokensOnly: false,
  tokenQuantity: 0,
  orderId: null,

  setStep: (step) => set({ currentStep: step }),
  nextStep: () => set((state) => ({
    currentStep: Math.min(state.currentStep + 1, TOTAL_STEPS),
  })),
  previousStep: () => set((state) => ({
    currentStep: Math.max(state.currentStep - 1, 1),
  })),
  setUserDetails: (email, fullName, phoneNumber) => set({ email, fullName, phoneNumber }),
  setWantsGifts: (wants) => set({ wantsGifts: wants }),
  setWantsSanta: (wants) => set({ wantsSanta: wants }),
  setWantsTokensOnly: (wants) => set({ wantsTokensOnly: wants }),
  setPickupLocation: (location) => set({ pickupLocation: location }),
  addGift: (gift) => set((state) => ({
    gifts: [...state.gifts, gift],
  })),
  removeGift: (index) => set((state) => ({
    gifts: state.gifts.filter((_, i) => i !== index),
  })),
  updateGift: (index, gift) => set((state) => ({
    gifts: state.gifts.map((g, i) => (i === index ? gift : g)),
  })),
  setSelectedTimeSlotId: (slotId) => set({ selectedTimeSlotId: slotId }),
  setSlotHeldUntil: (until) => set({ slotHeldUntil: until, slotExpired: false }),
  // Hold ran out: drop the slot and send the customer back to pick again
  expireSlot: () => set({ selectedTimeSlotId: null, slotHeldUntil: null, slotExpired: true, santaVisit: null, currentStep: 5 }),
  clearSlot: () => set({ selectedTimeSlotId: null, slotHeldUntil: null, slotExpired: false, santaVisit: null }),
  setSantaVisit: (visit) => set({ santaVisit: visit }),
  setTokenQuantity: (quantity) => set({ tokenQuantity: quantity }),
  setOrderId: (id) => set({ orderId: id }),
  reset: () => set({
    currentStep: 1,
    email: '',
    fullName: '',
    phoneNumber: '',
    wantsGifts: false,
    pickupLocation: null,
    gifts: [],
    wantsSanta: true,
    selectedTimeSlotId: null,
    slotHeldUntil: null,
    slotExpired: false,
    santaVisit: null,
    wantsTokensOnly: false,
    tokenQuantity: 0,
    orderId: null,
  }),
}))
